import { NestAPI } from '@marketing-milk/frontend'
import { AdCreative, AdCreativeReview, AdCreativeReviewDTO } from '@marketing-milk/interfaces'
import { useMutation, useQueryClient } from 'react-query'
import { commentsQueryKey } from './useComments'
import { packetReviewTrailsQueryPrefix } from './usePacketReviewTrails'

export interface CreativeVerdict {
  creativeID: number
  approved: boolean
  /** The reason, on a rejection. */
  notes?: string
}

interface CreativeReviewContext {
  /** Whether this actor must explicitly record the verdict as the client's. */
  asClientVerdict: boolean
  /** Whether this session may refetch the builder-only creative consumers. */
  hasBuilderAccess: boolean
  /**
   * The client contact an employee said they're reviewing with, for this visit.
   * Rides every verdict into its audit comment; the server ignores it on a
   * client's own verdict.
   */
  reviewedWith?: string | null
}

/**
 * Records client verdicts on a business's creatives, for the whole review page
 * rather than per card.
 *
 * Deliberately NOT scoped to one creative: the review surface renders ~50 cards
 * plus a modal, each of which can submit a verdict, and giving every one of them
 * its own mutation is how the grid's quick Approve and the modal's Reject drifted
 * apart in the first place. One owner means one request shape, one cohort
 * decision, and one invalidation policy.
 *
 * `asClientVerdict` is what the server needs to write the verdict as the client's.
 * An employee MUST send it — their verdict stands for the client's, in both
 * directions — and the server 400s without it rather than writing a review that
 * would read back as Draft.
 */
export const useAdCreativeReview = (
  businessID: number,
  { asClientVerdict, hasBuilderAccess, reviewedWith }: CreativeReviewContext
) => {
  const queryClient = useQueryClient()

  // Prefix-invalidate so sprint creatives, the creatives list, and
  // campaign-builder creative sources all refetch the new review status.
  // Also refresh the creative's comment thread, since a verdict posts an event
  // comment there, and the packet's review trails, which are read from it.
  //
  // Scoped to `['sprints', 'sprint-creatives']` (matching `useSprint`'s
  // `sprintCreatives` query key), NOT the bare `['sprints']` prefix — that
  // also matches the separate sprints-*list* query, which a client-review
  // user (no `AccessAdBuilderApp`) can't read.
  const invalidate = async (creativeID: number) => {
    const comments = commentsQueryKey(businessID, 'creative', String(creativeID))

    // A session without builder access can't read the general creatives list or
    // campaign-builder sources, and this page doesn't render them — but `useSprint`
    // unconditionally pulls in `useCreatives`, which keeps an AE-only
    // `GET .../ad-creatives` query active even here. Invalidating it on a client
    // account 401s and retries for several seconds, stalling this promise (react-query
    // v3 awaits `onSuccess` before settling `isLoading`) and with it the modal's
    // auto-advance. So the client gets the narrow set.
    if (!hasBuilderAccess) {
      await Promise.all([
        queryClient.invalidateQueries(['sprints', 'sprint-creatives']),
        queryClient.invalidateQueries(comments),
        queryClient.invalidateQueries(packetReviewTrailsQueryPrefix),
      ])
      return
    }

    await Promise.all([
      queryClient.invalidateQueries(['sprints', 'sprint-creatives']),
      queryClient.invalidateQueries(['creatives']),
      queryClient.invalidateQueries(['creatives-by-ids']),
      queryClient.invalidateQueries(comments),
      queryClient.invalidateQueries(packetReviewTrailsQueryPrefix),
    ])
  }

  const submitVerdict = useMutation(
    async ({ creativeID, approved, notes }: CreativeVerdict): Promise<AdCreativeReview> => {
      const dto: AdCreativeReviewDTO = {
        approved,
        notes,
        asClientVerdict,
        ...(reviewedWith ? { reviewedWith } : {}),
      }
      const { data } = await NestAPI.post<AdCreativeReview>(
        `businesses/${businessID}/ad-creatives/${creativeID}/reviews`,
        dto
      )
      return data
    },
    { onSuccess: (_review, { creativeID }) => invalidate(creativeID) }
  )

  // Employee-only, and the server enforces that independently. A client who
  // changes their mind submits a new verdict instead.
  const removeApproval = useMutation(
    async (creativeID: number): Promise<AdCreative> => {
      const { data } = await NestAPI.delete<AdCreative>(
        `businesses/${businessID}/ad-creatives/${creativeID}/reviews`
      )
      return data
    },
    { onSuccess: (_creative, creativeID) => invalidate(creativeID) }
  )

  return { submitVerdict, removeApproval }
}
